import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import '../styles/pages/PrivacyPage.css';

const SECTIONS = [
  { id: 'introducao',        title: 'Introdução' },
  { id: 'controlador',       title: 'Controlador de dados' },
  { id: 'dados-coletados',   title: 'Dados que coletamos' },
  { id: 'sensiveis',         title: 'Dados sensíveis de saúde' },
  { id: 'finalidades',       title: 'Como usamos seus dados' },
  { id: 'base-legal',        title: 'Bases legais' },
  { id: 'compartilhamento',  title: 'Compartilhamento e terceiros' },
  { id: 'sdks',              title: 'SDKs e serviços integrados' },
  { id: 'transferencia',     title: 'Transferência internacional' },
  { id: 'retencao',          title: 'Retenção e exclusão' },
  { id: 'exclusao-conta',    title: 'Como excluir sua conta e dados' },
  { id: 'seguranca',         title: 'Segurança' },
  { id: 'direitos',          title: 'Seus direitos (LGPD/GDPR/CCPA)' },
  { id: 'menores',           title: 'Privacidade de menores' },
  { id: 'cookies',           title: 'Cookies e tecnologias similares' },
  { id: 'alteracoes',        title: 'Alterações nesta política' },
  { id: 'contato',           title: 'Contato e DPO' },
];

const PrivacyPage = () => {
  return (
    <>
      <Helmet>
        <title>Política de Privacidade — LifeUp</title>
        <meta name="description" content="Política de Privacidade do LifeUp. Veja como coletamos, usamos e protegemos seus dados pessoais, em conformidade com LGPD, GDPR e CCPA." />
        <link rel="canonical" href="https://lifyup.com.br/privacidade" />
        <meta property="og:title" content="Política de Privacidade — LifeUp" />
        <meta property="og:description" content="Como protegemos seus dados — LGPD, GDPR, CCPA." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lifyup.com.br/privacidade" />
      </Helmet>

      <div className="lp-theme legal-page">
        <Header />

        <main className="legal-content">
          <div className="legal-header">
            <h1 className="legal-title">Política de Privacidade</h1>
            <p className="legal-updated">Versão 2.0 — vigente a partir de 1º de junho de 2026</p>
            <p className="legal-updated">Aplicável ao site lifyup.com.br, ao aplicativo móvel LifeUp e a todos os programas (Freedom, Balance e futuros)</p>
          </div>

          <div className="container">
            <nav className="legal-toc" aria-label="Índice da política de privacidade">
              <h3>Conteúdo</h3>
              <ol>
                {SECTIONS.map((s) => (
                  <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
                ))}
              </ol>
            </nav>

            <section id="introducao" className="legal-section">
              <h2>1. Introdução</h2>
              <p>
                A LifeUp ("LifeUp", "nós", "nosso") oferece uma plataforma digital de apoio à recuperação
                de dependências comportamentais — compulsões por pornografia (programa Freedom), apostas
                (programa Balance) e outras que venham a ser lançadas. Esta Política de Privacidade
                explica de forma clara como coletamos, usamos, compartilhamos e protegemos seus dados
                pessoais quando você utiliza nossos serviços web ou mobile.
              </p>
              <p>
                Esta política está em conformidade com a Lei Geral de Proteção de Dados Pessoais
                (LGPD — Lei 13.709/2018), o Regulamento Geral de Proteção de Dados europeu (GDPR
                — Regulation (EU) 2016/679), o California Consumer Privacy Act (CCPA), as políticas
                de privacidade da Google Play (User Data Policy) e da Apple App Store (App Store
                Review Guidelines, seção 5.1).
              </p>
            </section>

            <section id="controlador" className="legal-section">
              <h2>2. Controlador de dados</h2>
              <p>
                <strong>LifeUp Tecnologia Ltda.</strong><br />
                CNPJ: a ser informado<br />
                Endereço: a ser informado<br />
                E-mail: <a href="mailto:contato@lifyup.com.br">contato@lifyup.com.br</a>
              </p>
              <p>
                O Encarregado pelo Tratamento de Dados (DPO) é nominado na seção 17.
              </p>
            </section>

            <section id="dados-coletados" className="legal-section">
              <h2>3. Dados que coletamos</h2>
              <p>Coletamos somente o mínimo necessário para entregar e melhorar o serviço:</p>

              <h3>3.1 Dados fornecidos por você (cadastro e uso)</h3>
              <ul>
                <li><strong>Identificação:</strong> nome ou pseudônimo, e-mail, data de nascimento (verificação de 18+).</li>
                <li><strong>Autenticação:</strong> senha (sempre armazenada como hash bcrypt — nunca em texto puro).</li>
                <li><strong>Perfil de programa:</strong> programa em que você está inscrito (Freedom/Balance/outros), data de início, objetivos.</li>
                <li><strong>Conteúdo gerado:</strong> entradas de diário emocional, check-ins, anotações, mensagens em comunidades e chats.</li>
                <li><strong>Comunicação:</strong> mensagens trocadas com suporte ou enviadas em comunidades anônimas.</li>
              </ul>

              <h3>3.2 Dados coletados automaticamente</h3>
              <ul>
                <li><strong>Uso do app:</strong> telas visitadas, recursos utilizados, sequência de ações (somente analytics agregados).</li>
                <li><strong>Dispositivo:</strong> modelo, sistema operacional, versão do app, idioma, fuso horário.</li>
                <li><strong>Rede:</strong> endereço IP, operadora, país aproximado (sem geolocalização precisa).</li>
                <li><strong>Logs técnicos:</strong> erros, falhas e tempo de resposta — usados exclusivamente para diagnóstico.</li>
                <li><strong>Identificadores publicitários:</strong> NÃO coletamos IDFA (iOS) nem AAID (Android) para publicidade.</li>
              </ul>

              <h3>3.3 Dados que NÃO coletamos</h3>
              <ul>
                <li>Localização precisa (GPS).</li>
                <li>Contatos da agenda do dispositivo.</li>
                <li>Fotos, vídeos ou arquivos do dispositivo (exceto se você anexar voluntariamente).</li>
                <li>Histórico de navegação web fora do nosso aplicativo.</li>
                <li>Biometria.</li>
              </ul>
            </section>

            <section id="sensiveis" className="legal-section">
              <h2>4. Dados sensíveis de saúde</h2>
              <div className="warning-box">
                <p>
                  <strong>Atenção:</strong> informações sobre dependências, recaídas e estado emocional
                  são <strong>dados pessoais sensíveis</strong> (Art. 5º, II da LGPD; Art. 9º do GDPR).
                  Aplicamos proteções reforçadas:
                </p>
                <ul>
                  <li>Criptografia em repouso (AES-256) e em trânsito (TLS 1.3).</li>
                  <li>Acesso restrito por papel (RBAC) — somente sistemas autorizados consultam estes dados.</li>
                  <li>Nunca compartilhamos esses dados com anunciantes, seguradoras, empregadores ou plataformas de redes sociais.</li>
                  <li>Você pode usar pseudônimo — nome real não é exigido em nenhum ponto.</li>
                </ul>
              </div>
            </section>

            <section id="finalidades" className="legal-section">
              <h2>5. Como usamos seus dados</h2>
              <ul>
                <li>Operar a plataforma e entregar os programas contratados.</li>
                <li>Personalizar conteúdo, jornadas e recomendações dentro do programa em que você está inscrito.</li>
                <li>Permitir interação anônima em comunidades, fóruns e chats.</li>
                <li>Enviar notificações operacionais (check-in diário, emergência, lembretes opt-in).</li>
                <li>Garantir segurança, prevenir fraudes e detectar uso abusivo da plataforma.</li>
                <li>Conduzir análises agregadas e anônimas para melhoria contínua do produto.</li>
                <li>Cumprir obrigações legais e regulatórias.</li>
              </ul>
              <p><strong>Não usamos seus dados para:</strong> anúncios direcionados, venda a terceiros, perfis de crédito, ou treinamento de modelos de IA de terceiros.</p>
            </section>

            <section id="base-legal" className="legal-section">
              <h2>6. Bases legais para o tratamento</h2>
              <p>De acordo com o Art. 7º da LGPD e Art. 6º do GDPR:</p>
              <table className="legal-table">
                <thead>
                  <tr><th>Finalidade</th><th>Base legal</th></tr>
                </thead>
                <tbody>
                  <tr><td>Criação de conta e execução dos serviços</td><td>Execução de contrato</td></tr>
                  <tr><td>Tratamento de dados sensíveis (recaídas, diário)</td><td>Consentimento específico e destacado</td></tr>
                  <tr><td>Segurança, prevenção de fraude</td><td>Legítimo interesse</td></tr>
                  <tr><td>Comunicações de marketing (newsletters)</td><td>Consentimento (opt-in revogável)</td></tr>
                  <tr><td>Cumprimento de obrigações fiscais/legais</td><td>Obrigação legal</td></tr>
                </tbody>
              </table>
            </section>

            <section id="compartilhamento" className="legal-section">
              <h2>7. Compartilhamento e operadores terceiros</h2>
              <p>
                <strong>Não vendemos seus dados.</strong> Compartilhamos com operadores estritamente
                necessários ao funcionamento do serviço, sempre sob contrato de proteção de dados (DPA):
              </p>
              <table className="legal-table">
                <thead>
                  <tr><th>Operador</th><th>Finalidade</th><th>Dados</th><th>Localização</th></tr>
                </thead>
                <tbody>
                  <tr><td>Amazon Web Services (AWS) / Google Cloud</td><td>Hospedagem e banco de dados</td><td>Todos</td><td>EUA / Brasil</td></tr>
                  <tr><td>Stripe</td><td>Processamento de pagamentos (assinaturas)</td><td>Nome, e-mail, cartão (tokenizado)</td><td>EUA</td></tr>
                  <tr><td>Google Firebase Cloud Messaging</td><td>Notificações push</td><td>Token do dispositivo</td><td>EUA</td></tr>
                  <tr><td>Sendgrid / Resend</td><td>E-mails transacionais</td><td>E-mail, nome</td><td>EUA</td></tr>
                  <tr><td>Sentry</td><td>Monitoramento de erros</td><td>Logs técnicos, IP</td><td>EUA</td></tr>
                </tbody>
              </table>
              <p>
                Podemos divulgar dados se exigido por autoridade competente ou ordem judicial.
                Nesses casos, notificaremos você quando legalmente permitido.
              </p>
            </section>

            <section id="sdks" className="legal-section">
              <h2>8. SDKs e bibliotecas no aplicativo móvel</h2>
              <p>O app LifeUp utiliza os seguintes SDKs (refletidos no formulário de Data Safety da Google Play e Privacy Nutrition Labels da Apple):</p>
              <ul>
                <li><strong>Firebase Cloud Messaging</strong> — entrega de notificações push.</li>
                <li><strong>Capacitor</strong> — bridge nativa para o app híbrido.</li>
                <li><strong>Sentry</strong> — captura de crashes (anonimizado).</li>
              </ul>
              <p>
                NÃO usamos: Google Analytics for Firebase, Meta SDK, AppsFlyer, Adjust, TikTok SDK,
                ou qualquer SDK de atribuição publicitária.
              </p>
            </section>

            <section id="transferencia" className="legal-section">
              <h2>9. Transferência internacional</h2>
              <p>
                Alguns operadores (AWS, Stripe, Google) processam dados fora do Brasil. Garantimos
                que essas transferências obedecem aos requisitos da LGPD (Art. 33) e GDPR (Capítulo V),
                utilizando Cláusulas Contratuais Padrão (SCC) e provedores certificados (ISO 27001,
                SOC 2 Type II).
              </p>
            </section>

            <section id="retencao" className="legal-section">
              <h2>10. Retenção e descarte</h2>
              <table className="legal-table">
                <thead><tr><th>Tipo de dado</th><th>Prazo</th></tr></thead>
                <tbody>
                  <tr><td>Conta ativa</td><td>Enquanto durar a conta</td></tr>
                  <tr><td>Diário emocional e check-ins</td><td>Conta ativa + exclusão imediata mediante solicitação</td></tr>
                  <tr><td>Conta inativa (sem login)</td><td>24 meses, depois anonimizada</td></tr>
                  <tr><td>Logs técnicos</td><td>90 dias</td></tr>
                  <tr><td>Faturas e dados fiscais</td><td>5 anos (obrigação legal)</td></tr>
                </tbody>
              </table>
            </section>

            <section id="exclusao-conta" className="legal-section">
              <h2>11. Como excluir sua conta e seus dados</h2>
              <div className="deletion-box">
                <p>
                  <strong>Em conformidade com a política da Google Play (Account Deletion Requirement)
                  e Apple App Store Guideline 5.1.1(v)</strong>, oferecemos três formas de excluir
                  sua conta e dados associados:
                </p>
                <ol>
                  <li>
                    <strong>Dentro do aplicativo:</strong> menu <em>Configurações → Conta → Excluir
                    minha conta</em>. A exclusão é imediata.
                  </li>
                  <li>
                    <strong>Pelo site:</strong> acesse{' '}
                    <a href="https://lifyup.com.br/excluir-conta">lifyup.com.br/excluir-conta</a>{' '}
                    e siga as instruções (não exige login se você tiver acesso ao e-mail cadastrado).
                  </li>
                  <li>
                    <strong>Por e-mail:</strong> envie pedido para{' '}
                    <a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a> a partir do e-mail
                    cadastrado. Atenderemos em até 7 dias úteis.
                  </li>
                </ol>
                <p>
                  <strong>O que é excluído:</strong> perfil, diário, check-ins, mensagens, histórico
                  de uso, fotos, configurações de notificação e qualquer conteúdo gerado.
                </p>
                <p>
                  <strong>O que pode ser retido:</strong> registros fiscais (notas fiscais de pagamento)
                  pelo prazo legal de 5 anos, sempre desvinculados de você quando juridicamente possível.
                </p>
              </div>
            </section>

            <section id="seguranca" className="legal-section">
              <h2>12. Segurança da informação</h2>
              <ul>
                <li>Criptografia em trânsito (TLS 1.3) e em repouso (AES-256).</li>
                <li>Senhas armazenadas com hash bcrypt (custo ≥ 12).</li>
                <li>Autenticação de dois fatores (2FA) opcional via TOTP.</li>
                <li>Controle de acesso por papéis (RBAC) e princípio do menor privilégio.</li>
                <li>Backups diários criptografados e testados periodicamente.</li>
                <li>Auditoria de logs de segurança e monitoramento contínuo.</li>
                <li>Plano de resposta a incidentes — notificação à ANPD em até 48 horas em caso de incidente que possa gerar risco ou dano relevante (Art. 48 LGPD).</li>
              </ul>
            </section>

            <section id="direitos" className="legal-section">
              <h2>13. Seus direitos como titular</h2>
              <p>Você pode, a qualquer momento:</p>
              <ul>
                <li><strong>Confirmação e acesso</strong> — saber quais dados temos sobre você (Art. 18, I e II LGPD).</li>
                <li><strong>Correção</strong> — atualizar dados incompletos ou desatualizados.</li>
                <li><strong>Anonimização ou bloqueio</strong> — caso o tratamento seja desnecessário.</li>
                <li><strong>Eliminação</strong> — solicitar a exclusão (ver seção 11).</li>
                <li><strong>Portabilidade</strong> — receber seus dados em formato JSON/CSV.</li>
                <li><strong>Revogação de consentimento</strong> — sem prejuízo dos tratamentos anteriores.</li>
                <li><strong>Oposição</strong> — opor-se a tratamentos baseados em legítimo interesse.</li>
                <li><strong>Não submissão a decisões automatizadas</strong> — solicitar revisão humana.</li>
                <li><strong>Direitos GDPR:</strong> mesmos acima + reclamação a autoridade de controle no seu Estado-membro.</li>
                <li><strong>Direitos CCPA (residentes da Califórnia):</strong> direito de saber, deletar e opt-out de venda (não vendemos dados).</li>
              </ul>
              <p>Para exercer qualquer direito: <a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a> — responderemos em até 15 dias.</p>
            </section>

            <section id="menores" className="legal-section">
              <h2>14. Privacidade de menores de idade</h2>
              <p>
                O LifeUp é destinado exclusivamente a maiores de 18 anos. Não coletamos
                intencionalmente dados de pessoas menores de 18. Se identificarmos cadastro
                de menor, a conta será removida imediatamente.
              </p>
              <p>
                Em conformidade com o Children's Online Privacy Protection Act (COPPA) dos EUA
                e o Art. 14 da LGPD: caso você seja pai, mãe ou responsável e suspeite que um
                menor sob sua tutela criou conta na plataforma, entre em contato com{' '}
                <a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a> para remoção imediata.
              </p>
            </section>

            <section id="cookies" className="legal-section">
              <h2>15. Cookies e tecnologias similares</h2>
              <p>
                Usamos cookies essenciais (autenticação e segurança), de preferência (idioma,
                tema) e analytics agregados. Não usamos cookies publicitários nem de
                rastreamento entre sites.
              </p>
              <p>Detalhes completos: <Link to="/cookies">Política de Cookies</Link>.</p>
            </section>

            <section id="alteracoes" className="legal-section">
              <h2>16. Alterações nesta política</h2>
              <p>
                Podemos atualizar esta política periodicamente. Mudanças materiais (que afetem
                seus direitos ou as bases legais de tratamento) serão comunicadas por e-mail
                e por notificação dentro do app, com pelo menos 30 dias de antecedência. O
                número de versão e a data de vigência sempre aparecem no topo desta página.
              </p>
            </section>

            <section id="contato" className="legal-section">
              <h2>17. Contato e Encarregado pelo Tratamento de Dados (DPO)</h2>
              <div className="info-box">
                <p>
                  <strong>Encarregado pelo Tratamento de Dados (DPO):</strong> a ser nominado em
                  conjunto com a constituição da pessoa jurídica.<br />
                  <strong>E-mail do DPO:</strong> <a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a><br />
                  <strong>Privacidade geral:</strong> <a href="mailto:privacidade@lifyup.com.br">privacidade@lifyup.com.br</a><br />
                  <strong>Suporte ao usuário:</strong> <a href="mailto:contato@lifyup.com.br">contato@lifyup.com.br</a>
                </p>
                <p>
                  Você também pode peticionar diretamente à <strong>Autoridade Nacional de Proteção
                  de Dados (ANPD)</strong> em caso de violação dos seus direitos:{' '}
                  <a href="https://www.gov.br/anpd" target="_blank" rel="noopener">gov.br/anpd</a>.
                </p>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPage;
